import { format } from "date-fns";
import apiRequest from "../../../axios/useApi";
import { handleApiError } from "../../../lib/utils";
import { BookEZAccount } from "../BankingInterface";

export interface CustomerVendor {
  id: string;
  businessMasterId: string;
  externalId: string;
  name: string;
  recordType: string;
  createdDateTime: string;
}

export interface Option {
  label: string;
  value: string;
  other?: string;
  category?: string;
}

export interface TypeOptions {
  masterId: number;
  codeId: string;
  codeName: string;
}

export interface Categories {
  codeId: string;
  codeName: string;
  masterId: number;
}

export interface ApplyAndConfirmTransactionDataPayload {
  transactionId: string;
  typeOfTransaction: string;
  remarks: string;
  transactionAmount: number;
  splitTransacionDetails: {
    customerId: string | null;
    vendorId: string | null;
    categoryId: string | null;
    amount: number;
    description: string;
  }[];
}

export interface BulkConfirm {
  errorMsg: string;
  isSuccess: boolean;
  transactionId: string;
  voucherId: number;
}

export interface Transaction {
  amount: number;
  createdDateTime: string; // ISO 8601 format: "0001-01-01T00:00:00"
  isoCurrencyCode: string; // e.g., "USD"
  merchantName: string; // e.g., "Uber"
  personalFinanceCategoryConfidenceLevel: string;
  personalFinanceCategoryDetailed: string; // e.g., "TRANSPORTATION_TAXIS_AND_RIDE_SHARES"
  personalFinanceCategoryPrimary: string; // e.g., "TRANSPORTATION"
  categoryAccountId: string;
  categoryAccountName: string;
  plaidAccountId: string;
  recordType: number; // e.g., 46
  recordTypeName: string;
  transactionDate: string; // ISO 8601 format: "2025-06-01T00:00:00"
  transactionEntityTypeId: number;
  transactionId: string;
  transactionName: string;
  transactionType: number;
  updatedDateTime: string; // ISO 8601 format: "0001-01-01T00:00:00"
  remarks: string;
  customerId: string | null;
  vendorId: string | null;
  isAssigned: boolean;
  transactionDetailsCategoryModels: {
    customerId: string | null;
    vendorId: string | null;
    categoryId: string;
    amount: number;
    description: string;
    businessMasterId: string;
  }[];
}

export interface MatchTransactionPayload {
  transactionId: string;
  paymentDate: string; // ISO date string
  matchDetails: {
    invoiceId: string;
    amount: number;
  }[];
  matchCreditMemo: {
    creditMemoId: string;
    amount: number;
  }[];
}
export async function getCustomerVendorOptions(
  businessId: string,
  search: string
): Promise<Option[]> {
  const response = await apiRequest<CustomerVendor[]>({
    url: `/Plaid/${businessId}/GetCustomerAndVendor?Term=${encodeURIComponent(
      search
    )}`,
    method: "GET",
  });
  if (response && response.success && response.data) {
    return response.data.map((item) => ({
      label: item.name,
      value: item.id,
      other: item.recordType,
    }));
  }
  return [];
}

export async function getTransactionTypeOptions(
  businessId: string
): Promise<Option[]> {
  const response = await apiRequest<TypeOptions[]>({
    url: `/PlaidTransaction/${businessId}/GetTransactionType`,
    method: "GET",
  });
  if (response && response.success && response.data) {
    return response.data.map((item) => ({
      label: item.codeName,
      value: item.masterId.toString(),
    }));
  }
  return [];
}

export async function getTransactionRecordTypeOptions(
  businessId: string
): Promise<Option[]> {
  const response = await apiRequest<BookEZAccount[]>({
    url: `/account/${businessId}/AccountAutoComplete`,
    method: "GET",
  });
  if (response && response.success && response.data) {
    return response.data.map((item) => ({
      label: item.accountName,
      value: item.accountId,
      category: item.categoryName,
    }));
  }
  return [];
}

export async function confirmTransactionApi(
  businessId: string,
  selectedId: string
) {
  const response = await apiRequest<Categories>({
    url: `/PlaidTransaction/${businessId}/Confirm`,
    method: "POST",
    data: { transactionId: selectedId },
  });
  return response;
}

export async function getCategoriesApi(
  businessId: string
): Promise<Categories[]> {
  const response = await apiRequest<Categories[]>({
    url: `/Plaid/${businessId}/GetTransactionRecordType`,
    method: "GET",
  });
  if (response && response.success && response.data) {
    return response.data.sort((a, b) => a.masterId - b.masterId);
  }
  throw new Error(response?.message || "Failed to fetch categories");
}

export async function getCategoriesCountes(
  businessId: string,
  accountId: string,
  fromDate?: Date | null,
  toDate?: Date | null
): Promise<any> {
  const queryParams = new URLSearchParams({});
  if (accountId) queryParams.append("AccountId", accountId);
  if (fromDate) queryParams.append("FromDate", format(fromDate, "yyyy-MM-dd"));
  if (toDate) queryParams.append("ToDate", format(toDate, "yyyy-MM-dd"));

  const response = await apiRequest<Categories[]>({
    url: `/Plaid/${businessId}/GetTransactionSummary?${queryParams}`,
    method: "GET",
  });
  return response;
}

export async function getTransactionEdit(
  businessId: string,
  transactionId: string
): Promise<Transaction> {
  const response = await apiRequest<Transaction>({
    url: `/PlaidTransaction/${businessId}/GetPlaidTransactionEdit?TransactionId=${transactionId}`,
    method: "GET",
  });
  if (response && response.success && response.data) {
    return response.data;
  }
  throw new Error(response?.message || "Failed to fetch transaction");
}

export async function applyAndConfirmTransactionApi(
  businessId: string,
  data: ApplyAndConfirmTransactionDataPayload
) {
  const response = await apiRequest<any[]>({
    url: `/PlaidTransaction/${businessId}/ApplyAndConfirm`,
    method: "POST",
    data: data, // Pass the data object directly
  });

  return response;
}

export async function applyTransactionApi(
  businessId: string,
  data: ApplyAndConfirmTransactionDataPayload
) {
  const response = await apiRequest<any[]>({
    url: `/PlaidTransaction/${businessId}/Apply`,
    method: "POST",
    data: data, // Pass the data object directly
  });
  return response;
}

export async function excludeTransactionApi(
  businessId: string,
  data: string[]
) {
  const response = await apiRequest<any[]>({
    url: `/PlaidTransaction/${businessId}/Exclude`,
    method: "POST",
    data: {
      transactionId: data,
    }, // Pass the data object directly
  });
  if (response && response.success && response.data) {
    return response.data;
  }
  throw new Error(response?.message || "Failed to confirm trasnaction");
}

export async function bulkConfirmTransactionApi(
  businessId: string,
  selectedIds: string[]
) {
  const response = await apiRequest<BulkConfirm[]>({
    url: `/PlaidTransaction/${businessId}/BulkConfirm`,
    method: "POST",
    data: { transactionId: selectedIds }, // Example transaction IDs, replace with actual IDs
  });
  if (response && response.success) {
    return response.data;
  }
  throw new Error(response?.message || "Failed to confirm transaction");
}

export async function bulkUploadTransactionsUpload(
  accountId: string,
  data: any[],
  businessId: string,
  onError?: (error: Error) => void,
  handleApiError?: (response: any, onError?: (error: Error) => void) => void
) {
  try {
    const response = await apiRequest<any[]>({
      url: `PlaidTransaction/${businessId}/Upload?AccountId=${accountId}`,
      method: "POST",
      data: data,
    });

    if (response && response.success) {
      return { success: true, message: response?.message || "Undo successful" };
    } else {
      // Call handleApiError if provided
      if (handleApiError) handleApiError(response, onError);
      return { success: false, message: response?.message || "Undo failed" };
    }
  } catch (error: any) {
    if (handleApiError) handleApiError(error, onError);
    return { success: false, message: error?.message || "API error" };
  }
}

export async function undoCategorizedTransactionApi(
  businessId: string,
  selectedId: string,
  handleApiError?: (response: any, onError?: (error: Error) => void) => void,
  onError?: (error: Error) => void
): Promise<{ success: boolean; message?: string }> {
  try {
    const response = await apiRequest<any[]>({
      url: `/PlaidTransaction/${businessId}/Undo`,
      method: "POST",
      data: {
        transactionId: selectedId,
      },
    });

    if (response && response.success) {
      return { success: true, message: response?.message || "Undo successful" };
    } else {
      // Call handleApiError if provided
      if (handleApiError) handleApiError(response, onError);
      return { success: false, message: response?.message || "Undo failed" };
    }
  } catch (error: any) {
    if (handleApiError) handleApiError(error, onError);
    return { success: false, message: error?.message || "API error" };
  }
}

export async function fetchTransactionsApi({
  businessId,
  accountId,
  category,
  term,
  toDate,
  fromDate,
  pageNumber,
  pageSize,
}: {
  businessId: string;
  accountId: string;
  category: number | null;
  term: string;
  toDate: Date | null;
  fromDate: Date | null;
  pageNumber: number;
  pageSize: number;
}) {
  // Use parameter if provided, otherwise fallback to state/defaults
  const _accountId = accountId;
  const _category = category;
  const _term = term;
  const _fromDate = fromDate;
  const _toDate = toDate;
  const _pageNumber = pageNumber;
  const _pageSize = pageSize;

  const queryParams = new URLSearchParams({
    pageSize: _pageSize.toString(),
  });

  queryParams.append("accountId", _accountId);
  if (_category) queryParams.append("Category", _category.toString());
  if (_term) queryParams.append("Term", _term);
  if (_fromDate)
    queryParams.append("FromDate", format(_fromDate, "yyyy-MM-dd"));
  if (_toDate) queryParams.append("ToDate", format(_toDate, "yyyy-MM-dd"));
  if (_pageNumber) queryParams.append("pageNumber", _pageNumber.toString());

  const response = await apiRequest<{
    items: Transaction[];
    totalPages: number;
    totalRecords: number;
    pageNumber: number;
    pageSize: number;
  }>({
    url: `/Plaid/${businessId}/GetTransactionList/${_pageNumber}?${queryParams}`,
    method: "GET",
  });
  return response;
}

export async function MatchTransactionApi(
  payload: MatchTransactionPayload,
  businessId: string
) {
  const response = await apiRequest<any[]>({
    url: `/PlaidTransaction/${businessId}/Match`,
    method: "POST",
    data: payload,
  });
  return response;
}
